import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  User, Award, Briefcase, Lightbulb, Heart, 
  ArrowRight, CheckCircle2, BookOpen, Mic, Code
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const credentials = [
  { title: 'PMP', description: 'Project Management Professional' },
  { title: 'CCMP', description: 'Certified Change Management Professional' },
];

const expertise = [
  { icon: Briefcase, text: 'Project Management & Delivery' },
  { icon: Code, text: 'Technology & Software Development' },
  { icon: Heart, text: 'Emotional Intelligence & Leadership' },
  { icon: Lightbulb, text: 'Entrepreneurship & Business Strategy' },
  { icon: Mic, text: 'Public Speaking & Youth Mentorship' },
  { icon: BookOpen, text: 'Social-Emotional Learning (SEL)' },
];

const speakingTopics = [
  'Trauma Processing & Recovery',
  'The GRIND Framework (Greatness Requires Intense NonStop Drive)',
  'Self-Awareness & Decision Making',
  'Coping with FADDs (Fear, Anxiety, Doubt, Depression)',
  'Entrepreneurship & Business Building',
  'Technology & Innovation',
];

const founderSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "KaVonte Carthron",
  "jobTitle": "Founder & CEO",
  "worksFor": {
    "@type": "Organization",
    "name": "Fearless IT"
  },
  "description": "Tech entrepreneur, certified project manager (PMP, CCMP), speaker, and author. Creator of the FADDs of Life framework and Amazon #1 Bestseller 'The Book of Barz'.",
  "knowsAbout": ["Project Management", "Technology", "Entrepreneurship", "Social-Emotional Learning", "Public Speaking"],
  "sameAs": [
    "https://www.kavontecarthron.com",
    "https://linkedin.com/in/kavontecarthron",
    "https://twitter.com/kavontecarthron",
    "https://instagram.com/kavontecarthron"
  ]
};

const FounderPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="founder-page">
      <SEO 
        title="KaVonte Carthron - Founder of Fearless IT"
        description="Meet KaVonte Carthron, founder of Fearless IT. A certified PMP and CCMP with expertise in technology, entrepreneurship, and youth mentorship. Author of Amazon #1 Bestseller 'The Book of Barz'."
        keywords="KaVonte Carthron, Fearless IT founder, Las Vegas tech entrepreneur, PMP certified, CCMP certified, Book of Barz, FADDs of Life, social emotional learning speaker"
        url="/company/founder"
        structuredData={founderSchema}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-violet-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full mb-6">
            <User className="h-4 w-4 text-violet-600" />
            <span className="text-sm font-medium text-violet-700">Our Founder</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4" data-testid="founder-title">
            KaVonte Carthron
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-6" style={{ background: 'linear-gradient(to right, #7C3AED, #5B3DF5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Barz. Bytes. Business.
          </p>
          <p className="text-lg sm:text-xl text-slate-gray max-w-2xl mx-auto leading-relaxed">
            Tech entrepreneur, certified project manager, speaker, rapper, and author. Known as the Human HellCat, KaVonte embodies the fusion of creativity, technology, and business acumen.
          </p>
        </div>
      </section>

      {/* About & Credentials Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-6">
                The Visionary Behind Fearless IT
              </h2>
              <div className="space-y-4 text-slate-gray leading-relaxed">
                <p>
                  KaVonte Carthron is a dynamic force at the intersection of spoken word, technology, and entrepreneurship. As a seasoned Speaker, Rapper, and Tech Entrepreneur (SRT), he brings a unique blend of creativity and vision to every endeavor.
                </p>
                <p>
                  With certifications including PMP (Project Management Professional) and CCMP (Certified Change Management Professional), KaVonte combines technical expertise with emotional intelligence to lead transformative projects and mentor the next generation of leaders.
                </p>
                <p>
                  He is the creator of the FADDs of Life framework, helping people understand and overcome Fear, Anxiety, Doubt, and Depression. His work extends from corporate boardrooms to school auditoriums, where he uses hip-hop and storytelling to connect with audiences of all ages.
                </p>
              </div>
              
              {/* Credentials Badges */}
              <div className="mt-8 flex flex-wrap gap-4">
                {credentials.map((cred, idx) => (
                  <div key={idx} className="flex items-center gap-3 px-4 py-3 bg-violet-50 rounded-xl border border-violet-100">
                    <Award className="h-5 w-5 text-violet-600" />
                    <div>
                      <div className="font-bold text-violet-700">{cred.title}</div>
                      <div className="text-xs text-slate-gray">{cred.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Book Highlight */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-100">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-1">Amazon #1 Bestseller</div>
                    <h3 className="text-xl font-bold text-carbon-black mb-2">The Book of Barz</h3>
                    <p className="text-sm text-slate-gray leading-relaxed">
                      A transformative journey through Social-Emotional Learning, artfully weaving lived experiences into thought-provoking poetry using the GRIND (Greatness Requires Intense NonStop Drive) framework.
                    </p>
                    <a 
                      href="https://www.amazon.com/Book-Barz-Keep-GodWork/dp/B087LB9GGH"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-4 text-amber-600 font-semibold hover:text-amber-700 transition-colors"
                    >
                      Get the Book
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Expertise Grid */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-carbon-black mb-4">Areas of Expertise</h3>
                <div className="grid grid-cols-2 gap-3">
                  {expertise.map((item, idx) => {
                    const IconComponent = item.icon;
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <IconComponent className="h-4 w-4 text-violet-500 flex-shrink-0" />
                        <span className="text-sm text-slate-gray">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Speaking Topics Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full mb-4">
              <Mic className="h-4 w-4 text-violet-600" />
              <span className="text-sm font-medium text-violet-700">Speaker</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              Speaking Topics
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              KaVonte speaks at schools, conferences, and corporate events on topics that transform mindsets and inspire action.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {speakingTopics.map((topic, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                <CheckCircle2 className="h-5 w-5 text-violet-500 flex-shrink-0" />
                <span className="text-carbon-black font-medium">{topic}</span>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <a 
              href="https://www.kavontecarthron.com/contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-gradient-to-r from-violet-500 to-purple-500 text-white px-8 py-6 text-lg font-semibold rounded-xl hover:from-violet-600 hover:to-purple-600 transition-all">
                Book KaVonte to Speak
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Philosophy Quote Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-2xl p-8 sm:p-12 border border-violet-100 text-center">
            <blockquote className="text-xl sm:text-2xl font-medium text-carbon-black leading-relaxed mb-6">
              "With a passion for pushing boundaries, I continue to break new ground in both the Arts and Tech realms. Whether captivating audiences with performances or pioneering cutting-edge innovations, I remain a trailblazer, constantly pushing the limits of what's possible when Barz, Bytes, and Business collide."
            </blockquote>
            <div className="text-slate-gray font-medium">- KaVonte Carthron</div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-violet-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Connect With KaVonte
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            Whether you're looking for a technology partner, a speaker, or want to learn more about the FADDs of Life framework.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Contact Fearless IT
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a 
              href="https://www.kavontecarthron.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-violet-400 text-violet-600 hover:bg-violet-50 transition-all w-full sm:w-auto">
                Visit KaVonteCarthron.com
              </Button>
            </a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default FounderPage;
